import React, { useContext, useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import AuthContext from "../context/AuthContext";
import axiosInstance from "../api/axios";
import { useModal } from "../components/ModalManager";
import { useInfiniteScroll } from "../hooks/useInfiniteScroll";
import { useNavigate, useLocation } from "react-router-dom";
import ProfileCard from "../components/ProfileCard";
import { fetchUserProfile } from "../api/UserAPI";
import { deletePost } from "../api/PostAPI";
import PostCard from "../components/PostCard";
// import {
//   fetchFriendshipStatus,
//   fetchFriendRequestStatus,
//   createFriendRequest,
//   cancelFriendRequest,
// } from "../api/UserAPI";

function Profile() {
  const { user, token } = useContext(AuthContext);
  const { username } = useParams();
  const [profile, setProfile] = useState([]);
  // const [areFriends, setAreFriends] = useState(false);
  // const [friendRequest, setFriendRequest] = useState(null);
  const [posts, setPosts] = useState([]);

  const [profileLoading, setProfileLoading] = useState(false);
  const [postsLoading, setPostsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [lastFetchedTime, setLastFetchTime] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [hasFetched, setHasFetched] = useState(null);

  const POSTS_LIMIT = 9;
  const FETCH_DELAY = 1000;

  const navigate = useNavigate();
  const location = useLocation();
  const { openModal } = useModal();

  const [nextUrl, setNextUrl] = useState(`/profiles/${username}/posts/?limit=${POSTS_LIMIT}`);

  // init load
  useEffect(() => {
    if (!username || !token) return;

    const ac = new AbortController();
    let canceled = false;

    setProfileLoading(true);

    (async () => {
      try {
        const profileData = await fetchUserProfile(username, token);
        // const friendship = await fetchFriendshipStatus(username, token);
        if (canceled || ac.signal.aborted) return;
        setProfile(profileData);
        // setAreFriends(friendship.are_friends || false);

        // verifies that the user exists first before showing option to friend request
        // if not friends verify if there is a pending friend request
        // if (!friendship.are_friends) {
        //   // check for pending friend request
        //   const request = await fetchFriendRequestStatus(username, token);
        //   if (Array.isArray(request) && request.length > 0) {
        //     setFriendRequest(request[0]);
        //   }
        // }
      } catch (err) {
        if (!canceled) {
          console.error(err);
        }
      } finally {
        if (!canceled) {
          setProfileLoading(false);
        }
      }
    })();

    return () => {
      canceled = true;
      ac.abort();
    };
  }, [username, token]);

  // reset when username changes
  useEffect(() => {
    const initialUrl = `/profiles/${username}/posts/?limit=${POSTS_LIMIT}`;

    setPosts([]);
    setNextUrl(initialUrl);
    setPage(0);
    setLastFetchTime(0);
    setHasMore(true);
    setHasFetched(false);

    fetchUserPosts({ url: initialUrl, replace: true });
  }, [username]);

  // remove deleted posts when coming back from PostStatic
  useEffect(() => {
    const deletedPostId = location.state?.deletedPostId;
    if (deletedPostId == null) return;

    setPosts((prev) => prev.filter((post) => post.id !== deletedPostId));
  }, [location.state]);

  // Load-more handler for infinite scroll
  const handleLoadPosts = useCallback(() => {
    const now = Date.now();
    if (!nextUrl || postsLoading || now - lastFetchedTime < FETCH_DELAY) return;
    if (hasMore && hasFetched) {
      setPage((prev) => prev + 1);
      setHasFetched(false);
    }
  }, [nextUrl, postsLoading, lastFetchedTime, hasMore, hasFetched]);

  // useEffect(() => {
  //   if (areFriends) {
  //     setNextUrl(`/profiles/${username}/posts/?limit=9`);
  //     setPage(1); // to trigger the fetch correctly after setting nextUrl
  //   }
  // }, [areFriends]);

  // const handleFriendRequest = async () => {
  //   try {
  //     const res = await createFriendRequest(profile.id, token);
  //     setFriendRequest(res);
  //   } catch (err) {
  //     console.error("Failed to send friend request", err);
  //   }
  // };

  // const handleCancelFriendRequest = async () => {
  //   const data = await cancelFriendRequest(friendRequest.id, username, token);
  //   setFriendRequest(null);
  // };

  const loaderRef = useInfiniteScroll(handleLoadPosts);

  const fetchUserPosts = async ({ url, replace = false } = {}) => {
    const requestUrl = url || nextUrl;
    if (!requestUrl || postsLoading) return;

    try {
      setPostsLoading(true);

      const res = await axiosInstance.get(requestUrl, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });

      setPosts((prev) => {
        if (replace) return res.data.results;

        const existingIds = new Set(prev.map((p) => p.id));
        const newPosts = res.data.results.filter((p) => !existingIds.has(p.id));
        return [...prev, ...newPosts];
      });

      setNextUrl(res.data.next);
      setHasMore(!!res.data.next);
    } catch (err) {
      console.error("Error fetching user posts:", err);
    } finally {
      setHasFetched(true);
      setLastFetchTime(Date.now());
      setPostsLoading(false);
    }
  };

  // Fetch next page when infinite scroll increments page
  useEffect(() => {
    if (page === 0) return;
    fetchUserPosts();
  }, [page]);

  const handleOpenPost = (post) => {
    navigate(`/posts/${post.id}`, {
      state: { backgroundLocation: location },
    });
  };

  const handleDeletePost = async (postId) => {
    try {
      const res = await deletePost(postId);

      setPosts((prev) => prev.filter((post) => post.id !== postId));
    } catch (err) {
      console.error("Failed to delete post", err);
    }
  };

  return (
    <div>
      <ProfileCard profile={profile} loading={profileLoading} />
      <div className="max-w-4xl mx-auto p-4 border-t-2">
        {/* {profile.user === user.username && (
            <Link to="/saved" className="flex items-center space-x-2 hover:text-blue-500">
              <Bookmark />
              <span className="hidden xl:inline font-medium">Saved</span>
            </Link>
          )} */}
        {postsLoading && <p className="text-center">Loading...</p>}
        {posts.length === 0 && !postsLoading ? (
          <p className="text-center">No recent posts.</p>
        ) : (
          <div className="p-4 w-full mx-auto">
            {posts.map((post) => (
              <PostCard
                key={post.id}
                postData={post}
                user={user}
                focusPost={handleOpenPost}
                onDeletePost={handleDeletePost}
                token={token}
              />
            ))}
            <div ref={loaderRef} className="h-10"></div>
            {postsLoading && <p className="text-center">Loading...</p>}
          </div>
        )}
        {/* <div ref={loaderRef} className="h-[1px]" /> */}
      </div>
    </div>
  );
}

export default Profile;
